import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Spin,
  message,
  Divider,
  Button,
  Typography,
  Dropdown,
  Checkbox,
} from "antd";
import React, { useCallback, useMemo, useState } from "react";
import {
  PrinterOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import XLSX from "xlsx-js-style";
import {
  HEADERS_EXPORT_BEN,
  HEADERS_EXPORT_DETAIL,
  POST_LOAN_DB2_V2,
  POST_REPORT_IMPOUND,
} from "../../API/apiUrls";
import CurrencyFormat from "../../../hook/CurrencyFormat";
import "../../../assets/font/THSarabunNew-normal";
import "../../../assets/font/THSarabunNew-bold";
import MotionHoc from "../../../utils/MotionHoc";
import TokenCheck from "../../../hook/TokenCheck";
import ThaiDatePicker from "../../../hook/ThaiDatePicker";

const TwoLineHeader = ({ top, bottom }) => (
  <div style={{ textAlign: "center", lineHeight: 1.4 }}>
    <div>{top}</div>
    <div style={{ fontSize: 11, fontWeight: 400, color: "#888" }}>{bottom}</div>
  </div>
);

const Main = () => {
  const [
    currencyFormat,
    currencyFormatComma,
    currencyFormatPoint,
    currencyFormatNoPoint,
  ] = CurrencyFormat();
  const [signOut] = TokenCheck();
  const [loading, setLoading] = useState(false);
  const [arrayPay, setArrayPay] = useState([]);
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs().endOf("month"));
  const [isFlagAgreement, setIsFlagAgreement] = useState(true);
  const [isFlagEnforce, setIsFlagEnforce] = useState(true);

  // ── Query ─────────────────────────────────────────────
  const onQuery = () => {
    if (!startDate || !endDate) {
      message.warning("กรุณาเลือกวันที่ก่อนค้นหา");
      return;
    }
    queryData({
      date1: dayjs(startDate).format("YYYY-MM-DDTHH:mm:ssZ"),
      date2: dayjs(endDate).format("YYYY-MM-DDTHH:mm:ssZ"),
      currentStatus: 22,
    });
  };

  const queryData = async (payload) => {
    setLoading(true);
    try {
      const res = await axios.post(POST_REPORT_IMPOUND, payload, {
        headers: HEADERS_EXPORT_DETAIL,
      });
      if (res.status !== 200) {
        setArrayPay([]);
        return;
      }

      const reportData = res.data?.data ?? [];

      if (!reportData.length) {
        setArrayPay([]);
        return;
      }

      const groupedBySchema = reportData.reduce((acc, item) => {
        if (!item.CONTNO || !item.SCHEMA) return acc;
        if (!acc[item.SCHEMA]) acc[item.SCHEMA] = [];
        acc[item.SCHEMA].push(`'${item.CONTNO}'`);
        return acc;
      }, {});

      const schemaPayload = Object.entries(groupedBySchema).reduce(
        (acc, [schema, arr]) => {
          acc[schema] = arr.join(",");
          return acc;
        },
        {},
      );

      let db2Map = {};
      try {
        const db2Res = await axios.post(POST_LOAN_DB2_V2, schemaPayload, {
          headers: HEADERS_EXPORT_BEN,
        });
        const db2Data = Array.isArray(db2Res.data)
          ? db2Res.data
          : Array.isArray(db2Res.data?.data)
            ? db2Res.data.data
            : [];

        db2Data.forEach((item) => {
          const contno = item?.LOAN?.CONTNO;
          if (!contno) return;
          db2Map[contno] = {
            LOCAT: item.LOAN?.LOCAT,
            TOTPRC: item.LOAN?.TOTPRC,
            NCSHPRC: item.LOAN?.NCSHPRC,
            SMPAY: item.LOAN?.SMPAY,
            PRODUCTLOAN: item.LOAN?.PRODUCTLOAN,
            ISSUNO: item.LOAN?.ISSUNO,
            TYPE: item.MORTGAGE?.TYPE,
            MODEL: item.MORTGAGE?.MODEL,
            BAAB: item.MORTGAGE?.BAAB,
            COLOR: item.MORTGAGE?.COLOR,
            REGNO: item.MORTGAGE?.REGNO,
            ENGNO: item.MORTGAGE?.ENGNO,
            MANUYR: item.MORTGAGE?.MANUYR,
            DORECV: item.MORTGAGE?.DORECV,
            OFFIC: item.CUSTOMER?.OFFIC,
            OCCUP: item.CUSTOMER?.OCCUP,
            BIRTHDT: item.CUSTOMER?.BIRTHDT,
            ADDRESS: item.CUSTOMER?.ADDRESS ?? [],
            GUARANTORS: item.GUARANTORS ?? [],
          };
        });
      } catch (e) {
        console.warn("DB2 failed:", e);
      }
      setArrayPay(
        reportData
          .map((item) => ({ ...item, ...(db2Map[item.CONTNO] ?? {}) }))
          .sort((a, b) => dayjs(a.SDATE).valueOf() - dayjs(b.SDATE).valueOf()),
      );
    } catch (err) {
      message.error(
        err?.response?.status === 404 ? "ไม่พบข้อมูล" : "เกิดข้อผิดพลาด",
      );
      if (err?.response?.status === 401) {
        signOut();
      }
      setArrayPay([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterData = useCallback(() => {
    if (!arrayPay.length) return [];

    return arrayPay.filter((item) => {
      // ✅ ตัด closeContno = 1 ออกเสมอ
      if (item.closeContno === 1) return false;

      // ✅ ทั้งคู่ true → เอาทั้งหมด
      if (isFlagAgreement && isFlagEnforce) return true;

      // ✅ Agreement อย่างเดียว → flagLoanNew = 1
      if (isFlagAgreement && !isFlagEnforce) return item.flagLoanNew === 1;

      // ✅ Enforce อย่างเดียว → flagLoanNew = 2
      if (!isFlagAgreement && isFlagEnforce) return item.flagLoanNew === 2;

      // ✅ ทั้งคู่ false → ไม่มีข้อมูล
      return false;
    });
  }, [arrayPay, isFlagAgreement, isFlagEnforce]);

  // ── Columns ───────────────────────────────────────────
  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 60,
      render: (_, __, i) => i + 1,
    },
    {
      title: "สาขา",
      align: "center",
      width: 60,
      render: (_, r) => (
        <>
          <div style={{ fontWeight: 600 }}>{r.LOCAT || "-"}</div>
        </>
      ),
    },
    {
      title: "เลขสัญญา",
      key: "contract_model",
      align: "center",
      width: 130,
      render: (_, r) => (
        <>
          <div style={{ fontWeight: 600 }}>{r.newContno || "-"}</div>
        </>
      ),
    },
    {
      title: "ชื่อลูกค้า",
      key: "name_strno",
      align: "left",
      width: 140,
      render: (_, r) => (
        <div>
          {`${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`.trim() || "-"}
        </div>
      ),
    },
    {
      title: "รายละเอียดรถ",

      align: "left",
      width: 140,
      render: (_, r) => (
        <div>
          {r.MODEL} {r.BAAB || "-"}
        </div>
      ),
    },
    {
      title: "เลขตัวถัง",
      key: "STRNO",
      align: "left",
      width: 140,
      render: (_, r) => <div>{r.STRNO || "-"}</div>,
    },
    {
      title: "ทะเบียนรถ",
      key: "REGNO",
      align: "left",
      width: 140,
      render: (_, r) => <div>{r.REGNO || "-"}</div>,
    },
    {
      title: "วันที่ยึด",
      align: "center",
      width: 90,
      sorter: (a, b) =>
        dayjs(a.impoundDate).valueOf() - dayjs(b.impoundDate).valueOf(),
      render: (_, r) => (
        <>
          <div>
            {r.impoundDate
              ? dayjs(r.impoundDate).add(543, "year").format("DD/MM/YYYY")
              : "-"}
          </div>
        </>
      ),
    },
    {
      title: "ราคาขาย",
      dataIndex: "totalJugde",
      key: "totalJugde",
      align: "center",
      width: 70,

      render: (val) => <div>{currencyFormatPoint(val)}</div>,
    },
    {
      title: "ชำระแล้ว",
      key: "upay_vat",
      align: "right",
      width: 100,
      render: (_, r) => (
        <div>{currencyFormatPoint(r.SMPAY + r.SMPAY_AROTHR)}</div>
      ),
    },
    {
      title: "ค้าง",
      key: "totalJugde",
      align: "right",
      width: 90,
      sorter: (a, b) => (a.T_NOPAY_KONG ?? 0) - (b.T_NOPAY_KONG ?? 0),
      render: (_, r) => <div>{r.T_NOPAY_KONG}</div>,
    },
    {
      title: "เงินค้างชำระ",
      key: "credit_debtor",
      align: "right",
      width: 110,
      sorter: (a, b) => (a.creditDebtorOld ?? 0) - (b.creditDebtorOld ?? 0),
      render: (_, r) => (
        <div>
          {currencyFormatPoint(r.totalJugde - (r.SMPAY + r.SMPAY_AROTHR))}
        </div>
      ),
    },
    {
      title: "วันขาดสินธิ",
      key: "credit_debtor",
      align: "right",
      width: 110,
      sorter: (a, b) =>
        dayjs(a.impoundDate).valueOf() - dayjs(b.impoundDate).valueOf(),
      render: (_, r) => (
        <>
          {r.impoundDate
            ? dayjs(r.impoundDate)
                .add(543, "year")
                .add(45, "day")
                .format("DD/MM/YYYY")
            : "-"}
        </>
      ),
    },
  ];

  // ── Export Excel ──────────────────────────────────────
  const handleExportExcel = () => {
    if (!filteredData.length) {
      message.warning("ไม่มีข้อมูล");
      return;
    }

    const fromDate = startDate ? dayjs(startDate).format("DD/MM/YYYY") : "";
    const toDate = endDate ? dayjs(endDate).format("DD/MM/YYYY") : "";

    const rows = filteredData.map((r, i) => ({
      ลำดับ: i + 1,
      สาขา: r.LOCAT,
      เลขสัญญา: r.newContno || "",
      ชื่อลูกค้า: `${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`.trim(),
      รายละเอียดรถ: `${r.MODEL || ""} ${r.BAAB || ""}`.trim(),
      เลขตัวถัง: r.STRNO || "",
      ทะเบียนรถ: r.REGNO || "",
      วันที่ยึด: r.impoundDate
        ? dayjs(r.impoundDate).add(543, "year").format("DD/MM/YYYY")
        : "-",
      ราคาขาย: parseFloat(r.totalJugde) || 0,
      ชำระแล้ว: (r.SMPAY || 0) + (r.SMPAY_AROTHR || 0),
      ค้าง: r.T_NOPAY_KONG || 0,
      เงินค้างชำระ:
        (parseFloat(r.totalJugde) || 0) -
        ((r.SMPAY || 0) + (r.SMPAY_AROTHR || 0)),
      วันขาดสินธิ: r.impoundDate
        ? dayjs(r.impoundDate)
            .add(543, "year")
            .add(45, "day")
            .format("DD/MM/YYYY")
        : "-",
    }));

    const moneyKeys = ["ราคาขาย", "ชำระแล้ว", "ค้าง", "เงินค้างชำระ"];

    const summaryRow = {
      ลำดับ: "",
      สาขา: `รวมทั้งหมด ${filteredData.length} รายการ`,
      เลขสัญญา: "",
      ชื่อลูกค้า: "",
      รายละเอียดรถ: "",
      เลขตัวถัง: "",
      ทะเบียนรถ: "",
      วันที่ยึด: "",
      ราคาขาย: rows.reduce((s, r) => s + (r.ราคาขาย || 0), 0),
      ชำระแล้ว: rows.reduce((s, r) => s + (r.ชำระแล้ว || 0), 0),
      ค้าง: rows.reduce((s, r) => s + (r.ค้าง || 0), 0),
      เงินค้างชำระ: rows.reduce((s, r) => s + (r.เงินค้างชำระ || 0), 0),
      วันขาดสินธิ: "",
    };

    const allRows = [...rows, summaryRow];
    const ws = XLSX.utils.json_to_sheet(allRows);
    const keys = Object.keys(rows[0]);

    const borderStyle = {
      top: { style: "thin", color: { rgb: "CCCCCC" } },
      bottom: { style: "thin", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    };

    // header
    keys.forEach((_, ci) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: ci });
      if (!ws[cellRef]) return;
      ws[cellRef].s = {
        fill: { patternType: "solid", fgColor: { rgb: "344962" } },
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
        alignment: { horizontal: "center", vertical: "center" },
        border: borderStyle,
      };
    });

    // data rows
    rows.forEach((row, ri) => {
      const fill = {
        patternType: "solid",
        fgColor: { rgb: ri % 2 === 0 ? "EBF5FF" : "FFFFFF" },
      };
      keys.forEach((key, ci) => {
        const cellRef = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
        if (!ws[cellRef]) ws[cellRef] = { v: "", t: "s" };

        // ✅ กำหนด alignment แต่ละ column
        const getAlignment = (key) => {
          if (key === "ชื่อลูกค้า") return "left";
          if (["ราคาขาย", "ชำระแล้ว", "ค้าง", "เงินค้างชำระ"].includes(key))
            return "right";
          return "center"; // ✅ ที่เหลือทั้งหมดอยู่กลาง
        };

        const isMoney = moneyKeys.includes(key);

        ws[cellRef].s = {
          fill,
          font: { sz: 11 },
          alignment: {
            horizontal: getAlignment(key),
            vertical: "center",
          },
          border: borderStyle,
          numFmt: isMoney ? "#,##0.00" : undefined,
        };
      });
    });

    // summary row
    const summaryRowIdx = rows.length + 1;
    keys.forEach((key, ci) => {
      const cellRef = XLSX.utils.encode_cell({ r: summaryRowIdx, c: ci });
      if (!ws[cellRef]) return;

      const getAlignment = (key) => {
        if (key === "ชื่อลูกค้า") return "left";
        if (["ราคาขาย", "ชำระแล้ว", "ค้าง", "เงินค้างชำระ"].includes(key))
          return "right";
        return "center";
      };

      const isMoney = moneyKeys.includes(key);

      ws[cellRef].s = {
        fill: { patternType: "solid", fgColor: { rgb: "D5E8D4" } },
        font: { bold: true, sz: 12 },
        alignment: {
          horizontal: getAlignment(key),
          vertical: "center",
        },
        border: borderStyle,
        numFmt: isMoney ? "#,##0.00" : undefined,
      };
    });

    ws["!cols"] = keys.map((key) => ({ wch: Math.max(key.length * 2.5, 16) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "รายงาน");
    XLSX.writeFile(wb, `รายงานรถยึดรอไถ่ถอน_${fromDate}_${toDate}.xlsx`);
    message.success("Export Excel สำเร็จ");
  };

  // ── Export PDF ────────────────────────────────────────
  const handlePrintPDF = () => {
    if (!filteredData.length) {
      message.warning("ไม่มีข้อมูล");
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const now = dayjs().format("DD/MM/YYYY HH:mm:ss");
    const fromDate = startDate ? dayjs(startDate).format("DD/MM/YYYY") : "";
    const toDate = endDate ? dayjs(endDate).format("DD/MM/YYYY") : "";

    const drawHeader = (pageNum) => {
      doc.setFont("THSarabunNew", "bold");
      doc.setFontSize(16);
      doc.text("รายงานรถยึดรอไถ่ถอน", pageWidth / 2, 8, { align: "center" });
      doc.text(
        `ยึดจากวันที่  ${fromDate}   ถึงวันที่  ${toDate}`,
        pageWidth / 2,
        14,
        { align: "center" },
      );
      doc.setFont("THSarabunNew", "normal");
      doc.setFontSize(14);
      doc.text("บริษัท วัน เอเอ็มซี จำกัด (สำนักงานใหญ่)", 8, 20);
      doc.text(`วันที่พิมพ์ ${now}`, 8, 26);
      doc.text(`หน้าที่ ${pageNum}`, pageWidth - 8, 26, { align: "right" });
    };

    const tableBody = filteredData.map((r, i) => [
      i + 1,
      r.LOCAT || "-",
      r.newContno || "-",
      `${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`.trim(),
      `${r.MODEL || ""} ${r.BAAB || ""}`.trim(),
      r.STRNO || "-",
      r.REGNO || "-",
      r.impoundDate
        ? dayjs(r.impoundDate).add(543, "year").format("DD/MM/YYYY")
        : "-",
      currencyFormatPoint(r.totalJugde),
      currencyFormatPoint((r.SMPAY || 0) + (r.SMPAY_AROTHR || 0)),
      r.T_NOPAY_KONG || 0,
      currencyFormatPoint(
        (parseFloat(r.totalJugde) || 0) -
          ((r.SMPAY || 0) + (r.SMPAY_AROTHR || 0)),
      ),
      r.impoundDate
        ? dayjs(r.impoundDate)
            .add(543, "year")
            .add(45, "day")
            .format("DD/MM/YYYY")
        : "-", // วันขาดสินธิ
    ]);

    const t = filteredData.reduce(
      (acc, r) => ({
        totalJugde: acc.totalJugde + (parseFloat(r.totalJugde) || 0),
        SMPAY_ALL: acc.SMPAY_ALL + ((r.SMPAY || 0) + (r.SMPAY_AROTHR || 0)),
        remain:
          acc.remain +
          ((parseFloat(r.totalJugde) || 0) -
            ((r.SMPAY || 0) + (r.SMPAY_AROTHR || 0))),
      }),
      { totalJugde: 0, SMPAY_ALL: 0, remain: 0 },
    );

    const summaryRow = [
      {
        content: `รวมทั้งหมด ${filteredData.length} รายการ`,
        colSpan: 8,
        styles: {
          fontStyle: "bold",
          font: "THSarabunNew",
          fillColor: [236, 240, 241],
        },
      },
      {
        content: currencyFormatPoint(t.totalJugde),
        styles: {
          halign: "right",
          fontStyle: "bold",
          font: "THSarabunNew",
          fillColor: [236, 240, 241],
        },
      },
      {
        content: currencyFormatPoint(t.SMPAY_ALL),
        styles: {
          halign: "right",
          fontStyle: "bold",
          font: "THSarabunNew",
          fillColor: [236, 240, 241],
        },
      },
      {
        content: "",
        styles: { fillColor: [236, 240, 241] },
      },
      {
        content: currencyFormatPoint(t.remain),
        styles: {
          halign: "right",
          fontStyle: "bold",
          font: "THSarabunNew",
          fillColor: [236, 240, 241],
        },
      },
      {
        content: "",
        styles: { fillColor: [236, 240, 241] },
      },
    ];

    doc.autoTable({
      startY: 30,
      head: [
        [
          "ลำดับ",
          "สาขา",
          "เลขสัญญา",
          "ชื่อลูกค้า",
          "รายละเอียดรถ",
          "เลขตัวถัง",
          "ทะเบียนรถ",
          "วันที่ยึด",
          "ราคาขาย",
          "ชำระแล้ว",
          "ค้าง",
          "เงินค้างชำระ",
          "วันขาดสินธิ",
        ],
      ],
      body: [...tableBody, summaryRow],
      headStyles: {
        font: "THSarabunNew",
        fillColor: [52, 73, 94],
        textColor: 255,
        halign: "center",
        fontStyle: "bold",
        fontSize: 13,
      },
      columnStyles: {
        3: { halign: "left" }, // ชื่อลูกค้า
        4: { halign: "left" }, // รายละเอียดรถ
        5: { halign: "left" }, // เลขตัวถัง
        8: { halign: "right" }, // ราคาขาย
        9: { halign: "right" }, // ชำระแล้ว
        10: { halign: "center" }, // ค้าง
        11: { halign: "right" }, // เงินค้างชำระ
      },
      tableWidth: "auto", // ✅ ขยายเต็มหน้าอัตโนมัติ
      margin: { top: 30, left: 3, right: 3 },
      alternateRowStyles: { fillColor: [235, 245, 255] },
      styles: {
        font: "THSarabunNew",
        fontSize: 12,
        cellPadding: 1,
        halign: "center",
        fillColor: [255, 255, 255],
      },
      didDrawPage: () =>
        drawHeader(doc.internal.getCurrentPageInfo().pageNumber),
    });

    doc.save(`รายงานรถยึดรอไถ่ถอน_${fromDate}_${toDate}.pdf`);
  };

  // ✅ Dropdown menu เลือก PDF หรือ Excel
  const exportMenuItems = {
    items: [
      {
        key: "pdf",
        label: "Export PDF",
        icon: <FilePdfOutlined style={{ color: "#c0392b" }} />,
      },
      {
        key: "excel",
        label: "Export Excel",
        icon: <FileExcelOutlined style={{ color: "#27ae60" }} />,
      },
    ],
    onClick: ({ key }) => {
      if (key === "pdf") handlePrintPDF();
      if (key === "excel") handleExportExcel();
    },
  };

  const hasDate = startDate && endDate;
  const filteredData = useMemo(() => handleFilterData(), [handleFilterData]);
  console.log("filteredData", filteredData);
  console.log("filteredData", filteredData);

  return (
    <>
      <style>{`
      .row-even td { background-color: #f5f8ff !important; }
      .row-odd td  { background-color: #ffffff !important; }
    `}</style>
      <Spin spinning={loading} size="large" tip="Loading...">
        <Row>
          <Col span={16}>
            <Space style={{ marginBottom: 10 }}>
              เริ่ม
              <ThaiDatePicker
                size="large"
                value={startDate}
                onChange={setStartDate}
                placeholder={"วันที่เริ่มต้น"}
                allowClear={false}
              />
              ถึง
              <ThaiDatePicker
                size="large"
                value={endDate}
                onChange={setEndDate}
                placeholder={"วันที่สิ้นสุด"}
                allowClear={false}
              />
              <Button size="large" type="primary" onClick={onQuery}>
                ค้นหา
              </Button>
              <Checkbox
                checked={isFlagAgreement}
                onChange={(e) => setIsFlagAgreement(e.target.checked)}
              >
                ทำยอม
              </Checkbox>
              <Checkbox
                checked={isFlagEnforce}
                onChange={(e) => setIsFlagEnforce(e.target.checked)}
              >
                พิพากษา
              </Checkbox>
            </Space>
          </Col>
          <Col span={8} style={{ textAlign: "end" }}>
            <Space style={{ marginBottom: 10 }}>
              {/* ✅ Dropdown เลือก PDF / Excel */}
              <Dropdown menu={exportMenuItems} disabled={!filteredData.length}>
                <Button size="large" icon={<PrinterOutlined />}>
                  พิมพ์ / Export <DownOutlined />
                </Button>
              </Dropdown>
            </Space>
          </Col>
        </Row>

        <Card style={{ marginBottom: 10 }}>
          <Divider style={{ fontSize: 20 }}>รายงานรถยึดรอไถ่ถอน</Divider>

          <Typography.Text
            strong
            style={{ display: "block", textAlign: "center", fontSize: 16 }}
          >
            บริษัท วัน เอเอ็มซี จำกัด (สำนักงานใหญ่)
          </Typography.Text>

          {hasDate && (
            <Typography.Text
              style={{ display: "block", textAlign: "center", fontSize: 16 }}
            >
              ยึดจากวันที่ {dayjs(startDate).format("DD/MM/YYYY")} ถึงวันที่{" "}
              {dayjs(endDate).format("DD/MM/YYYY")}
            </Typography.Text>
          )}

          <Table
            style={{ marginTop: 10 }}
            size="small"
            columns={columns}
            dataSource={filteredData}
            rowKey={(r) => r.idJudge ?? r.CONTNO}
            rowClassName={(_, index) =>
              index % 2 === 0 ? "row-even" : "row-odd"
            }
            scroll={{ x: 1400 }}
            pagination={{
              defaultPageSize: 50,
              showSizeChanger: true,
              showTotal: (t) => `ทั้งหมด ${t} รายการ`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            summary={() => {
              const t = filteredData.reduce(
                (acc, r) => ({
                  totalJugde: acc.totalJugde + (parseFloat(r.totalJugde) || 0),
                  SMPAY_ALL:
                    acc.SMPAY_ALL + ((r.SMPAY || 0) + (r.SMPAY_AROTHR || 0)),
                  T_NOPAY_KONG: acc.T_NOPAY_KONG + (r.T_NOPAY_KONG || 0),
                  remain:
                    acc.remain +
                    ((parseFloat(r.totalJugde) || 0) -
                      ((r.SMPAY || 0) + (r.SMPAY_AROTHR || 0))),
                }),
                { totalJugde: 0, SMPAY_ALL: 0, T_NOPAY_KONG: 0, remain: 0 },
              );

              const cs = { fontWeight: "bold", background: "#ecf0f1" };
              const cr = {
                fontWeight: "bold",
                background: "#ecf0f1",
                textAlign: "right",
              };

              return (
                <Table.Summary.Row>
                  {/* ลำดับ, สาขา, เลขสัญญา, ชื่อ, รายละเอียดรถ, เลขตัวถัง, ทะเบียน, วันที่ยึด */}
                  <Table.Summary.Cell colSpan={8} align="left" style={cs}>
                    รวมทั้งหมด {filteredData.length} รายการ
                  </Table.Summary.Cell>

                  {/* ราคาขาย (totalJugde) */}
                  <Table.Summary.Cell align="right" style={cr}>
                    {currencyFormatPoint(t.totalJugde)}
                  </Table.Summary.Cell>

                  {/* ชำระแล้ว (SMPAY + SMPAY_AROTHR) */}
                  <Table.Summary.Cell align="right" style={cr}>
                    {currencyFormatPoint(t.SMPAY_ALL)}
                  </Table.Summary.Cell>

                  {/* ค้าง (T_NOPAY_KONG) */}
                  <Table.Summary.Cell align="right" style={cr}>
                    {t.T_NOPAY_KONG}
                  </Table.Summary.Cell>

                  {/* เงินค้างชำระ */}
                  <Table.Summary.Cell align="right" style={cr}>
                    {currencyFormatPoint(t.remain)}
                  </Table.Summary.Cell>

                  {/* วันขาดสินธิ */}
                  <Table.Summary.Cell style={cs} />
                </Table.Summary.Row>
              );
            }}
          />
        </Card>
      </Spin>
    </>
  );
};

const MainImpoundCar = MotionHoc(Main);
export default MainImpoundCar;
